import { cloneElement, createContext, forwardRef, isValidElement, useContext, useId, useMemo, type HTMLAttributes, type ReactElement, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { Icon } from '../../icons/Icon';
import { useOptionalFormContext } from '../Form/FormContext';
import { zodFieldMeta } from '../utils/zodFieldMeta';
import { useDsT } from '../../services/i18n';

export type FieldOrientation = 'vertical' | 'horizontal';

/** Character (or item) counter rendered under the control, e.g. `{ count: 12, max: 200 }` → "12 / 200". */
export interface FieldCounter {
  count: number;
  max?: number;
}

/** Props handed to a render-function child: `<Field name="qty">{(f) => <NumberInput value={f.value} onValueChange={f.onChange} … />}</Field>`. */
export interface FieldRenderProps<V = unknown> {
  id: string;
  name: string;
  value: V;
  onChange: (value: V) => void;
  onBlur: () => void;
  invalid: boolean;
  error?: string;
  touched: boolean;
  required: boolean;
  /** Space-separated ids of description / hint / error for `aria-describedby`. */
  describedBy?: string;
}

/** W29: forms that know every field's requiredness (the metadata renderer, FormGrid) show "optional" on the rest. */
export const FieldOptionalHintContext = createContext<string | null>(null);

/** How many of the 12 grid columns a field takes inside a `FormGrid` (full width in a narrow container). */
export type FieldSpan = 3 | 4 | 6 | 8 | 12;

/** Container breakpoints of the `FormGrid` (a narrow card stacks, a wide page spreads). */
const spanClass: Record<FieldSpan, string> = { 3: '@lg:col-span-6 @3xl:col-span-3', 4: '@lg:col-span-6 @3xl:col-span-4', 6: '@lg:col-span-6', 8: '@lg:col-span-8', 12: '@lg:col-span-12' };

export interface FieldProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'title'> {
  label?: ReactNode;
  /** Dot path in the enclosing form; binds value / error / touched from `form.field(name)`. */
  name?: string;
  /** Explicit control id; defaults to the form field id or a generated one. */
  htmlFor?: string;
  /** Help text rendered between label and control. */
  description?: ReactNode;
  /** Short hint rendered under the control (replaced by the error while invalid). */
  hint?: ReactNode;
  /** Explicit error; overrides the form error for this field. */
  error?: ReactNode;
  /** Required marker; derived from the zod schema when omitted and bound to a form. */
  required?: boolean;
  /** Text rendered next to the label when the field is not required (e.g. "Optional"). */
  optionalLabel?: ReactNode;
  /** Columns of a surrounding `FormGrid` (W29); ignored outside one. */
  span?: FieldSpan;
  orientation?: FieldOrientation;
  /** Label and control share one row (checkbox / small inputs). */
  inline?: boolean;
  /** Character / item counter under the control; turns danger past `max`. */
  counter?: FieldCounter;
  /** Present the value as text instead of rendering the control. */
  readOnly?: boolean;
  /** Text shown while `readOnly`; defaults to the child element's `value` prop, or the form value. */
  readOnlyValue?: ReactNode;
  /** Text shown while `readOnly` and the value is empty. */
  emptyText?: ReactNode;
  children?: ReactNode | ((props: FieldRenderProps) => ReactNode);
}

type ControlElement = ReactElement<Record<string, unknown>>;

function joinIds(...ids: Array<string | undefined | false | null>): string | undefined {
  const list = new Set(ids.flatMap((v) => (typeof v === 'string' ? v.split(/\s+/).filter(Boolean) : [])));
  return list.size ? Array.from(list).join(' ') : undefined;
}

export const Field = forwardRef<HTMLDivElement, FieldProps>(function Field(
  {
    label,
    name,
    htmlFor,
    description,
    hint,
    error: errorProp,
    required: requiredProp,
    optionalLabel: optionalLabelProp,
    span,
    orientation = 'vertical',
    inline,
    counter,
    readOnly,
    readOnlyValue,
    emptyText = '—',
    className,
    children,
    ...rest
  },
  ref,
) {
  const form = useOptionalFormContext();
  const optionalHint = useContext(FieldOptionalHintContext);
  const optionalLabel = optionalLabelProp ?? optionalHint;
  const reactId = useId();
  const t = useDsT();
  const binding = form && name ? form.field(name) : null;
  const element = isValidElement(children) ? (children as ControlElement) : null;
  const childId = typeof element?.props.id === 'string' ? element.props.id : undefined;
  const id = htmlFor ?? childId ?? binding?.id ?? `${reactId}-field`;

  const required = useMemo(() => {
    if (requiredProp !== undefined) return requiredProp;
    if (!form || !name) return false;
    const meta = zodFieldMeta(form.schema, name);
    // Booleans are never "required": an unchecked box is a valid value, not a missing one.
    return meta.found && meta.required && meta.type !== 'boolean';
  }, [form, name, requiredProp]);

  const error = errorProp ?? binding?.error;
  const invalid = !!error;
  const descriptionId = description != null ? `${id}-description` : undefined;
  const hintId = hint != null && !invalid ? `${id}-hint` : undefined;
  const errorId = invalid ? `${id}-error` : undefined;
  const counterId = counter ? `${id}-counter` : undefined;
  const describedBy = joinIds(errorId, descriptionId, hintId, counterId);
  const overLimit = !!counter && typeof counter.max === 'number' && counter.count > counter.max;

  let control: ReactNode;
  if (readOnly) {
    const raw = readOnlyValue ?? (element && element.props.value != null ? element.props.value : binding?.value);
    const shown: ReactNode = raw == null || raw === '' ? emptyText : Array.isArray(raw) ? raw.join(', ') : typeof raw === 'object' && !isValidElement(raw) ? JSON.stringify(raw) : (raw as ReactNode);
    control = (
      <p id={id} aria-describedby={describedBy} className={cn('zo-field-readonly m-0 flex min-h-control items-center text-fg', (raw == null || raw === '') && 'text-fg-subtle')}>
        {shown}
      </p>
    );
  } else if (typeof children === 'function') {
    const renderProps: FieldRenderProps = {
      id,
      name: binding?.name ?? name ?? id,
      value: binding?.value,
      onChange: binding?.onChange ?? (() => undefined),
      onBlur: binding?.onBlur ?? (() => undefined),
      invalid,
      touched: binding?.touched ?? false,
      required,
      describedBy,
    };
    if (typeof error === 'string') renderProps.error = error;
    control = children(renderProps);
  } else if (element) {
    const injected: Record<string, unknown> = {
      id,
      'aria-describedby': joinIds(element.props['aria-describedby'] as string | undefined, describedBy),
    };
    if (invalid) {
      injected['aria-invalid'] = true;
      if (typeof element.type !== 'string') injected.invalid = true;
    }
    if (required) injected.required = true;
    control = cloneElement(element, injected);
  } else {
    control = children;
  }

  const labelClass = cn('zo-field-label inline-flex flex-wrap items-baseline gap-1 text-sm font-medium text-fg', orientation === 'horizontal' && !inline && 'sm:pt-1.5');
  const labelNode =
    label != null && readOnly ? (
      <span className={labelClass}>{label}</span>
    ) : label != null ? (
      <label htmlFor={id} className={labelClass}>
        <span>{label}</span>
        {required ? (
          <>
            <span aria-hidden="true" className="text-danger-fg">
              *
            </span>
            <span className="sr-only">{t('ds.common.requiredMark', '(required)')}</span>
          </>
        ) : optionalLabel != null ? (
          <span className="type-caption font-normal text-fg-subtle">{optionalLabel}</span>
        ) : null}
      </label>
    ) : null;

  const message = invalid ? (
    <p id={errorId} role="alert" className="zo-field-error flex items-start gap-1.5 text-sm text-danger-fg">
      <Icon name="alert-circle" size={14} className="mt-0.5 shrink-0" />
      {error}
    </p>
  ) : hint != null ? (
    <p id={hintId} className="zo-field-hint text-sm text-fg-muted">
      {hint}
    </p>
  ) : null;

  const counterNode = counter ? (
    <span id={counterId} aria-live="polite" data-over={overLimit || undefined} className={cn('zo-field-counter ms-auto shrink-0 text-xs tnum', overLimit ? 'text-danger-fg' : 'text-fg-muted')}>
      {counter.count}
      {typeof counter.max === 'number' ? ` / ${counter.max}` : ''}
    </span>
  ) : null;

  const messages =
    message || counterNode ? (
      <div className="zo-field-messages flex items-start justify-between gap-3">
        {message ?? <span />}
        {counterNode}
      </div>
    ) : null;

  return (
    <div
      ref={ref}
      data-orientation={orientation}
      data-inline={inline || undefined}
      data-invalid={invalid || undefined}
      data-required={required || undefined}
      data-readonly={readOnly || undefined}
      className={cn(
        'zo-field-root min-w-0',
        span && cn('col-span-12', spanClass[span]),
        inline ? 'flex flex-wrap items-center gap-x-3 gap-y-1' : orientation === 'horizontal' ? 'grid gap-x-4 gap-y-1 sm:grid-cols-3 sm:items-start' : 'flex flex-col gap-1',
        className,
      )}
      {...rest}
    >
      {labelNode}
      <div className={cn('zo-field-control flex min-w-0 flex-col gap-1', orientation === 'horizontal' && !inline && 'sm:col-span-2', inline && 'flex-1')}>
        {descriptionId ? (
          <p id={descriptionId} className="zo-field-description text-sm text-fg-muted">
            {description}
          </p>
        ) : null}
        {control}
        {messages}
      </div>
    </div>
  );
});

/** Alias of `Field` for code written against the form-library naming. */
export const FormField = Field;
